import {Component, OnInit} from '@angular/core';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {BuildingService} from '../building/building.service';
import {ChannelChargeMgmtComponent} from '../channel/channel-charge-mgmt.component';
import {ExportationModalService} from '../exportation/exportation-modal.service';
import {FileService} from '../file/file.service';
import {CombiFormComponent} from '../shared/combi-form.component';
import {UIRouter} from '@uirouter/angular';
import {Assignment} from './assignment.model';
import {TranslateService} from '@ngx-translate/core';
import {AssignmentService} from './assignment.service';
import {UserService} from '../user/user.service';
import {User} from '../user/user.model';
import {RequestParams} from '@ngx-ivengi/crud';
import {EnumerationService} from '../enumeration/enumeration.service';
import {RoutePrefix} from '../shared/route-prefix';
import {FilterParam} from '@ngx-ivengi/crud/request-params';
import {BsModalService} from 'ngx-bootstrap';
import {ChannelAddExistingComponent} from '../channel/channel-add-existing.component';
import {Contact} from '../contact/contact.model';
import {Warranty} from '../warranty/warranty.model';
import {ChannelBulkUpdateModalService} from '../channel/channel-bulk-update-modal.service';
import {ChannelService} from '../channel/channel.service';

enum ChannelSubView {
  LIST = 'list',
  CHARGE_MGMT = 'chargemgmt'
}

@Component({
  selector: 'app-assignment-form',
  templateUrl: './assignment-form.component.html'
})
export class AssignmentFormComponent extends CombiFormComponent implements OnInit {
  public initialised: boolean = false;
  public assignment: Assignment = new Assignment();
  public assignmentType: string;
  public formTitle: string;
  public projectManagers: User[] = [];
  public mechanics: User[] = [];
  public manufacturers: string[];
  public warranties: Warranty[] = [];
  public contacts: Contact[] = [];
  public assignmentStatus: string[];
  public assignmentState: string;
  public numberingTypes: string[];
  public routePrefix: RoutePrefix;
  public customerContactFilter: FilterParam = {id: 0, key: 'type', operator: 'eq', value: 'CUSTOMER'};
  public areContactTypesLinked: boolean = false;
  public areBuildingsLinked: boolean = false;
  public showWarrantyAddButton: boolean = false;
  public activeTab: string = (localStorage.getItem('assignmentTab')) ? localStorage.getItem('assignmentTab') : 'assignment';
  public channelSubView: ChannelSubView = ChannelSubView.LIST;
  public ChannelSubView = ChannelSubView;
  protected stateIdParam: string = 'assignmentId';
  protected filterTimeout;

  public channelIds: string[];
  public channelFilters: BehaviorSubject<{[key: string]: any}> = new BehaviorSubject<{[key: string]: any}>({});
  private filtersObj: {[key: string]: any} = {};

  constructor(
    protected router: UIRouter,
    protected assignmentService: AssignmentService,
    protected userService: UserService,
    private enumService: EnumerationService,
    private buildingService: BuildingService,
    private exportationModalService: ExportationModalService,
    private channelBulkUpdateModalService: ChannelBulkUpdateModalService,
    private translate: TranslateService,
    private fileService: FileService,
    private modalService: BsModalService,
    private channelService: ChannelService,
  ) {
    super(router);
  }

  public filter(event) {
    // only delete a property from filterObj if specifically set to falsy
    let timeout: number = 0;

    if(event.currentTarget.name === 'showOnlyTerminated' && event.currentTarget.checked) {
      this.filtersObj.deletedStatus = 'TERMINATED';
    } else if (event.currentTarget.name === 'showOnlyTerminated' && !event.currentTarget.checked) {
      delete this.filtersObj.deletedStatus;
    }
    if(event.currentTarget.name === 'channelNumbers' && event.currentTarget.value) {
      timeout = 500;
      this.filtersObj.channelNumbers = event.currentTarget.value;
    } else if (event.currentTarget.name === 'channelNumbers' && !event.currentTarget.value) {
      delete this.filtersObj.channelNumbers;
    }

    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = null;
    }

    this.filterTimeout = setTimeout(() => {
      this.channelFilters.next(this.filtersObj);
    }, timeout);
  }

  // showWarrantyAddButton
  public onWarrantiesLoaded(warranties) {
    this.warranties = warranties.items;
    this.showWarrantyAddButton = (warranties.items.length < (this.contacts.length + 1)); // +1 for general warranty
  }
  public onContactsLoaded(contacts) {
    this.contacts = contacts.items;
    this.areContactTypesLinked = (contacts.items.length > 0);
    this.showWarrantyAddButton = (this.warranties.length < (contacts.items.length + 1)); // +1 for general warranty
  }

  public onBuildingsLoaded(buildings) {
    this.areBuildingsLinked = (buildings.length > 0);
  }

  public ngOnInit() {
    super.ngOnInit();

    this.channelService.channelIds.subscribe((channelIds) => {
      this.channelIds = channelIds;
    });

    this.initialised = true;

    this.enumService.get('manufacturer').subscribe((manufacturers) => {
      this.manufacturers = manufacturers;
    });

    this.enumService.get('assignment-status').subscribe((assignmentStatus) => {
      this.assignmentStatus = assignmentStatus;
    });

    this.enumService.get('assignment-numbering-type').subscribe((numberingTypes) => {
      this.numberingTypes = numberingTypes;
    });

    const projectManagerRequest = new RequestParams();
    projectManagerRequest.pagination.limit = 0;
    projectManagerRequest.addFilterParam({
      id: 0,
      key: 'roles',
      value: 'PROJECTMANAGER',
      operator: 'eq',
      filterFn: 'substringof',
      filterOutcome: 'true'
    });

    this.userService.getAll(projectManagerRequest).subscribe((projectManagers: User[]) => {
      this.projectManagers = projectManagers;
    });

    const mechanicRequest = new RequestParams();
    mechanicRequest.pagination.limit = 0;
    mechanicRequest
      .addFilterParam({
      id: 0,
      key: 'roles',
      value: 'MECHANIC',
      operator: 'eq',
        filterFn: 'substringof',
        filterOutcome: 'true'
    });

    this.userService.getAll(mechanicRequest).subscribe((mechanics: User[]) => {
      this.mechanics = mechanics.sort((a, b) => {
        switch (true) {
          case a.enabled && !b.enabled: {
            return -1;
          }

          case !a.enabled && b.enabled: {
            return 1;
          }

          case a.enabled && b.enabled:
          default: {
            return 0;
          }
        }
      });
    });

    const matches = this.router.stateService.$current.name.match(/^\w+\.(\w+).*$/);
    this.assignmentType = matches[1] || '';
    this.assignment.setType(this.assignmentType);

    switch(this.formMode) {
      case this.FormMode.ADD : {
        this.translate.get('assignment.form.add.blockTitle').subscribe((translation) => {
          this.formTitle = translation;
        });
      } break;

      case this.FormMode.UPDATE : {
        this.routePrefix = {
          entity: 'assignment',
          entityId: this.router.stateService.params[this.stateIdParam]
        };


        this.assignmentService.get(this.router.stateService.params[this.stateIdParam]).map((assignment: Assignment) => {
          assignment.setFileRecordingSrc(this.fileService.getLinkById(assignment.fileRecordingId));
          assignment.setFileWorksheetSrc(this.fileService.getLinkById(assignment.fileWorksheetId));
          return assignment;
        }).subscribe((assignment: Assignment) => {
          this.assignment = assignment;
        });

        this.translate.get('assignment.form.update.blockTitle').subscribe((translation) => {
          this.formTitle = translation;
        });
      } break;
    }
  }

  public save(assignmentState) {
    this.assignment.status = assignmentState.split(': ')[1];

    switch(this.formMode) {
      case this.FormMode.ADD : {
        this.assignmentService.add(this.assignment).subscribe((response) => {
          const assignment = new Assignment();
          assignment.fromResponse(response);
          this.router.stateService.go(`assignment.${this.assignmentType}.edit`, {assignmentId: assignment.id}, {reload: true});
        });
      } break;

      case this.FormMode.UPDATE : {
        this.assignmentService.update(this.assignment).subscribe((response) => {
          this.router.stateService.go(`assignment.${this.assignmentType}`, this.router.stateService.params, {reload: true});
        });
      } break;
    }
  }

  public channelExportation() {
    this.exportationModalService.showExportModal({
      assignmentId: [this.router.stateService.params[this.stateIdParam]]
    });
  }

  public bulkUpdate() {
    this.channelBulkUpdateModalService.showBulkUpdateModal({
      assignmentId: [this.router.stateService.params[this.stateIdParam]],
      channelIds: this.channelIds,
    });
  }

  public channelAddExisting() {
    this.buildingService.clearRoutePrefix();
    this.buildingService.setRoutePrefix(`assignment/${this.router.stateService.params.assignmentId}`);

    const bsModalRef = this.modalService.show(ChannelAddExistingComponent, {animated: true, keyboard: true, backdrop: true, ignoreBackdropClick: false, class: 'brc-modal-full' });
    bsModalRef.content.data = {bsModalRef};

    bsModalRef.content.onLink().subscribe(() => {
      this.buildingService.clearRoutePrefix();
      bsModalRef.hide();
    });
  }

  public chargeMgmt() {
    const bsModalRef = this.modalService.show(ChannelChargeMgmtComponent, {animated: true, keyboard: true, backdrop: true, ignoreBackdropClick: false, class: 'brc-modal-full' });
    bsModalRef.content.data = {bsModalRef};
  }

  public onTabSelect(tab) {
    localStorage.setItem('assignmentTab', tab);
  }

  public fileRecordingChange(evt) {
    this.assignment.setFileRecording(evt.target.files);
  }

  public fileWorksheetChange(evt) {
    this.assignment.setFileWorksheet(evt.target.files);
  }

}



// WEBPACK FOOTER //
// /var/jenkins_home/workspace/brands-compart-portal-frontend/checkout/src/app/components/assignment/assignment-form.component.ts